/**
 * Connection Store
 *
 * Manages user connections ("friends list") for the authenticated user.
 * Includes WebSocket real-time updates for connection events.
 *
 * Follows the pattern established by invitation-store.ts
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';
import { connectionsApi } from '@/services/api/connections';
import { navigateToSafeAppPath } from '@/components/notifications/notificationNavigation';
import { socketClient } from '@/services/websocket/socketClient';
import { toast } from 'sonner';
import type {
  UserConnection,
  PendingConnectionRequest,
  SentConnectionRequest,
  UserSearchResult,
  BlockedUser,
  ConnectionRequestReceivedPayload,
  ConnectionAcceptedPayload,
  ConnectionDeclinedPayload,
  ConnectionCancelledPayload,
  ConnectionRemovedPayload,
} from '@/types/connections';
import * as errorUtils from '@/utils/errors';

// ============================================================================
// WebSocket Event Types (must match backend)
// ============================================================================

export const CONNECTION_EVENTS = {
  REQUEST_RECEIVED: 'connection:request_received',
  REQUEST_ACCEPTED: 'connection:accepted',
  REQUEST_DECLINED: 'connection:declined',
  REQUEST_CANCELLED: 'connection:cancelled',
  CONNECTION_REMOVED: 'connection:removed',
} as const;

const logBackgroundConnectionError = (
  operation: string,
  error: unknown
): void => {
  console.error(
    '[ConnectionStore] background action failed:',
    operation,
    errorUtils.getErrorMessageOrFallback(error, 'Unexpected connection error')
  );
};

// ============================================================================
// Store State Interface
// ============================================================================

interface ConnectionState {
  // Data
  connections: UserConnection[];
  pendingReceived: PendingConnectionRequest[];
  pendingSent: SentConnectionRequest[];
  searchResults: UserSearchResult[];
  blockedUsers: BlockedUser[];

  // Counts
  connectionCount: number;
  pendingReceivedCount: number;
  pendingSentCount: number;
  blockedUsersCount: number;

  // Loading states
  isLoading: boolean;
  isSearching: boolean;

  // Error state
  error: string | null;

  // Cache
  lastFetched: number | null;
  lastPendingReceivedFetched: number | null;
  lastPendingSentFetched: number | null;
  lastBlockedFetched: number | null;

  // Actions
  fetchConnections: (force?: boolean) => Promise<void>;
  fetchPendingReceived: (force?: boolean) => Promise<void>;
  fetchPendingSent: () => Promise<void>;
  fetchBlockedUsers: () => Promise<void>;
  sendRequest: (userId: string, message?: string) => Promise<void>;
  acceptRequest: (connectionId: string) => Promise<void>;
  declineRequest: (connectionId: string) => Promise<void>;
  cancelRequest: (connectionId: string) => Promise<void>;
  removeConnection: (connectionId: string) => Promise<void>;
  blockUser: (userId: string) => Promise<void>;
  unblockUser: (userId: string) => Promise<void>;
  searchUsers: (query: string, limit?: number) => Promise<void>;
  clearSearchResults: () => void;
  clearError: () => void;
  reset: () => void;

  // Internal WebSocket handlers
  _handleRequestReceived: (payload: ConnectionRequestReceivedPayload) => void;
  _handleRequestAccepted: (payload: ConnectionAcceptedPayload) => void;
  _handleRequestDeclined: (payload: ConnectionDeclinedPayload) => void;
  _handleRequestCancelled: (payload: ConnectionCancelledPayload) => void;
  _handleConnectionRemoved: (payload: ConnectionRemovedPayload) => void;
}

// ============================================================================
// Constants
// ============================================================================

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// ============================================================================
// Store Implementation
// ============================================================================

export const useConnectionStore = create<ConnectionState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      connections: [],
      pendingReceived: [],
      pendingSent: [],
      searchResults: [],
      blockedUsers: [],

      connectionCount: 0,
      pendingReceivedCount: 0,
      pendingSentCount: 0,
      blockedUsersCount: 0,

      isLoading: false,
      isSearching: false,

      error: null,

      lastFetched: null,
      lastPendingReceivedFetched: null,
      lastPendingSentFetched: null,
      lastBlockedFetched: null,

      // ========================================================================
      // Fetch Actions
      // ========================================================================

      fetchConnections: async (force = false) => {
        const { lastFetched, isLoading } = get();

        // Skip if already loading
        if (isLoading) {
          console.log('[ConnectionStore] fetchConnections - skipping, already loading');
          return;
        }

        // Skip if recently fetched (within cache duration)
        if (!force && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
          console.log('[ConnectionStore] fetchConnections - using cached data');
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          console.log('[ConnectionStore] fetchConnections - fetching from API...');
          const response = await connectionsApi.getConnections();

          const connections = Array.isArray(response.data) ? response.data : [];

          set((state) => {
            state.connections = connections;
            state.connectionCount = connections.length;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });

          console.log('[ConnectionStore] fetchConnections - found:', connections.length);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to fetch connections';

          console.error('[ConnectionStore] fetchConnections - error:', errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
        }
      },

      fetchPendingReceived: async (force = false) => {
        const { lastPendingReceivedFetched, isLoading } = get();

        if (isLoading) {
          console.log('[ConnectionStore] fetchPendingReceived - skipping, already loading');
          return;
        }

        if (
          !force &&
          lastPendingReceivedFetched &&
          Date.now() - lastPendingReceivedFetched < CACHE_DURATION
        ) {
          console.log('[ConnectionStore] fetchPendingReceived - using cached data');
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          console.log('[ConnectionStore] fetchPendingReceived - fetching from API...');
          const response = await connectionsApi.getPendingReceived();

          const pending = Array.isArray(response.data) ? response.data : [];

          set((state) => {
            state.pendingReceived = pending;
            state.pendingReceivedCount = pending.length;
            state.isLoading = false;
            state.lastPendingReceivedFetched = Date.now();
          });

          console.log('[ConnectionStore] fetchPendingReceived - found:', pending.length);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to fetch pending requests';

          console.error('[ConnectionStore] fetchPendingReceived - error:', errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
        }
      },

      fetchPendingSent: async () => {
        const { lastPendingSentFetched, isLoading } = get();

        if (isLoading) {
          console.log('[ConnectionStore] fetchPendingSent - skipping, already loading');
          return;
        }

        if (
          lastPendingSentFetched &&
          Date.now() - lastPendingSentFetched < CACHE_DURATION
        ) {
          console.log('[ConnectionStore] fetchPendingSent - using cached data');
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          console.log('[ConnectionStore] fetchPendingSent - fetching from API...');
          const response = await connectionsApi.getPendingSent();

          const pending = Array.isArray(response.data) ? response.data : [];

          set((state) => {
            state.pendingSent = pending;
            state.pendingSentCount = pending.length;
            state.isLoading = false;
            state.lastPendingSentFetched = Date.now();
          });

          console.log('[ConnectionStore] fetchPendingSent - found:', pending.length);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to fetch sent requests';

          console.error('[ConnectionStore] fetchPendingSent - error:', errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
        }
      },

      fetchBlockedUsers: async () => {
        const { lastBlockedFetched, isLoading } = get();

        if (isLoading) {
          console.log('[ConnectionStore] fetchBlockedUsers - skipping, already loading');
          return;
        }

        if (
          lastBlockedFetched &&
          Date.now() - lastBlockedFetched < CACHE_DURATION
        ) {
          console.log('[ConnectionStore] fetchBlockedUsers - using cached data');
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          console.log('[ConnectionStore] fetchBlockedUsers - fetching from API...');
          const response = await connectionsApi.getBlockedUsers();

          const blockedUsers = response.data?.blockedUsers ?? [];

          set((state) => {
            state.blockedUsers = blockedUsers;
            state.blockedUsersCount = blockedUsers.length;
            state.isLoading = false;
            state.lastBlockedFetched = Date.now();
          });

          console.log('[ConnectionStore] fetchBlockedUsers - found:', blockedUsers.length);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to fetch blocked users';

          console.error('[ConnectionStore] fetchBlockedUsers - error:', errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
        }
      },

      // ========================================================================
      // Action Methods
      // ========================================================================

      sendRequest: async (userId: string, message?: string) => {
        console.log('[ConnectionStore] sendRequest - userId:', userId);

        set((state) => {
          state.error = null;
        });

        try {
          await connectionsApi.sendRequest(userId, message);

          // Invalidate cache and refetch
          set((state) => {
            state.lastPendingSentFetched = null;
          });

          await get().fetchPendingSent();

          console.log('[ConnectionStore] sendRequest - success');
          toast.success('Connection request sent');
        } catch (error: unknown) {
          const errorMessage = errorUtils.getErrorMessageOrFallback(
            error,
            'Failed to send connection request',
          );

          console.error('[ConnectionStore] sendRequest - error:', errorMessage);

          // If the error is "already pending", refresh the pending sent list
          // to update the UI with the existing pending request
          const isAlreadyPending = errorUtils.hasErrorCode(error, 'CONFLICT_ERROR') ||
            errorMessage.toLowerCase().includes('already pending');

          if (isAlreadyPending) {
            console.log('[ConnectionStore] sendRequest - refreshing pending sent due to conflict');
            set((state) => {
              state.lastPendingSentFetched = null;
            });
            await get().fetchPendingSent();
            // Don't show error toast for "already pending" - just update UI
            return;
          }

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      acceptRequest: async (connectionId: string) => {
        console.log('[ConnectionStore] acceptRequest - connectionId:', connectionId);

        // Optimistic update - find and remove from pending
        const request = get().pendingReceived.find(
          (r) => r.connectionId === connectionId
        );

        set((state) => {
          state.pendingReceived = state.pendingReceived.filter(
            (r) => r.connectionId !== connectionId
          );
          state.pendingReceivedCount = state.pendingReceived.length;
          state.error = null;
        });

        try {
          await connectionsApi.acceptRequest(connectionId);

          // Invalidate cache and refetch connections
          set((state) => {
            state.lastFetched = null;
            state.lastPendingReceivedFetched = null;
          });

          await get().fetchConnections();

          console.log('[ConnectionStore] acceptRequest - success');
          // Note: Toast is shown by the calling component with personalized message
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to accept request';

          console.error('[ConnectionStore] acceptRequest - error:', errorMessage);

          // Rollback optimistic update
          if (request) {
            set((state) => {
              state.pendingReceived.unshift(request);
              state.pendingReceivedCount = state.pendingReceived.length;
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      declineRequest: async (connectionId: string) => {
        console.log('[ConnectionStore] declineRequest - connectionId:', connectionId);

        // Optimistic update
        const request = get().pendingReceived.find(
          (r) => r.connectionId === connectionId
        );

        set((state) => {
          state.pendingReceived = state.pendingReceived.filter(
            (r) => r.connectionId !== connectionId
          );
          state.pendingReceivedCount = state.pendingReceived.length;
          state.error = null;
        });

        try {
          await connectionsApi.declineRequest(connectionId);

          set((state) => {
            state.lastPendingReceivedFetched = null;
          });

          console.log('[ConnectionStore] declineRequest - success');
          // Note: Toast is shown by the calling component with personalized message
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to decline request';

          console.error('[ConnectionStore] declineRequest - error:', errorMessage);

          // Rollback optimistic update
          if (request) {
            set((state) => {
              state.pendingReceived.unshift(request);
              state.pendingReceivedCount = state.pendingReceived.length;
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      cancelRequest: async (connectionId: string) => {
        console.log('[ConnectionStore] cancelRequest - connectionId:', connectionId);

        // Optimistic update
        const request = get().pendingSent.find(
          (r) => r.connectionId === connectionId
        );

        set((state) => {
          state.pendingSent = state.pendingSent.filter(
            (r) => r.connectionId !== connectionId
          );
          state.pendingSentCount = state.pendingSent.length;
          state.error = null;
        });

        try {
          await connectionsApi.cancelRequest(connectionId);

          set((state) => {
            state.lastPendingSentFetched = null;
          });

          console.log('[ConnectionStore] cancelRequest - success');
          toast.success('Connection request cancelled');
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to cancel request';

          console.error('[ConnectionStore] cancelRequest - error:', errorMessage);

          // Rollback optimistic update
          if (request) {
            set((state) => {
              state.pendingSent.unshift(request);
              state.pendingSentCount = state.pendingSent.length;
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      removeConnection: async (connectionId: string) => {
        console.log('[ConnectionStore] removeConnection - connectionId:', connectionId);

        // Optimistic update
        const connection = get().connections.find(
          (c) => c.connectionId === connectionId
        );

        set((state) => {
          state.connections = state.connections.filter(
            (c) => c.connectionId !== connectionId
          );
          state.connectionCount = state.connections.length;
          state.error = null;
        });

        try {
          await connectionsApi.removeConnection(connectionId);

          set((state) => {
            state.lastFetched = null;
          });

          console.log('[ConnectionStore] removeConnection - success');
          toast.success('Connection removed');
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to remove connection';

          console.error('[ConnectionStore] removeConnection - error:', errorMessage);

          // Rollback optimistic update
          if (connection) {
            set((state) => {
              state.connections.push(connection);
              state.connectionCount = state.connections.length;
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      blockUser: async (userId: string) => {
        console.log('[ConnectionStore] blockUser - userId:', userId);

        set((state) => {
          state.error = null;
        });

        try {
          await connectionsApi.blockUser(userId);

          // Invalidate all caches
          set((state) => {
            state.lastFetched = null;
            state.lastPendingReceivedFetched = null;
            state.lastPendingSentFetched = null;
            state.lastBlockedFetched = null;
          });

          // Refetch connections to remove blocked user
          await get().fetchConnections();
          // Refresh blocked users list
          await get().fetchBlockedUsers();

          console.log('[ConnectionStore] blockUser - success');
          toast.success('User blocked');
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to block user';

          console.error('[ConnectionStore] blockUser - error:', errorMessage);

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      unblockUser: async (userId: string) => {
        console.log('[ConnectionStore] unblockUser - userId:', userId);

        // Optimistic update - remove from blocked list
        const blockedUser = get().blockedUsers.find(
          (b) => b.user.id === userId
        );

        set((state) => {
          state.blockedUsers = state.blockedUsers.filter(
            (b) => b.user.id !== userId
          );
          state.blockedUsersCount = state.blockedUsers.length;
          state.error = null;
        });

        try {
          await connectionsApi.unblockUser(userId);

          // Invalidate cache
          set((state) => {
            state.lastBlockedFetched = null;
          });

          console.log('[ConnectionStore] unblockUser - success');
          toast.success('User unblocked');
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to unblock user';

          console.error('[ConnectionStore] unblockUser - error:', errorMessage);

          // Rollback optimistic update
          if (blockedUser) {
            set((state) => {
              state.blockedUsers.unshift(blockedUser);
              state.blockedUsersCount = state.blockedUsers.length;
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      // ========================================================================
      // Search
      // ========================================================================

      searchUsers: async (query: string, limit = 20) => {
        console.log('[ConnectionStore] searchUsers - query:', query, 'limit:', limit);

        if (!query.trim()) {
          set((state) => {
            state.searchResults = [];
          });
          return;
        }

        set((state) => {
          state.isSearching = true;
          state.error = null;
        });

        try {
          const response = await connectionsApi.searchUsers(query, limit);

          const results = Array.isArray(response.data) ? response.data : [];

          set((state) => {
            state.searchResults = results;
            state.isSearching = false;
          });

          console.log('[ConnectionStore] searchUsers - found:', results.length);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to search users';

          console.error('[ConnectionStore] searchUsers - error:', errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isSearching = false;
          });
        }
      },

      clearSearchResults: () => {
        console.log('[ConnectionStore] clearSearchResults');
        set((state) => {
          state.searchResults = [];
        });
      },

      // ========================================================================
      // Utility Actions
      // ========================================================================

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      reset: () => {
        console.log('[ConnectionStore] reset - clearing all state');
        set((state) => {
          state.connections = [];
          state.pendingReceived = [];
          state.pendingSent = [];
          state.searchResults = [];
          state.connectionCount = 0;
          state.pendingReceivedCount = 0;
          state.pendingSentCount = 0;
          state.isLoading = false;
          state.isSearching = false;
          state.error = null;
          state.lastFetched = null;
          state.lastPendingReceivedFetched = null;
          state.lastPendingSentFetched = null;
        });
      },

      // ========================================================================
      // WebSocket Event Handlers (Internal)
      // ========================================================================

      _handleRequestReceived: (payload: ConnectionRequestReceivedPayload) => {
        console.log('[ConnectionStore] WebSocket: Request received', payload);

        set((state) => {
          // Check if request already exists
          const exists = state.pendingReceived.some(
            (r) => r.connectionId === payload.connectionId
          );

          if (!exists) {
            const newRequest: PendingConnectionRequest = {
              connectionId: payload.connectionId,
              requester: payload.requester,
              message: payload.message,
              requestedAt: payload.requestedAt,
              expiresAt: payload.expiresAt,
            };

            state.pendingReceived.unshift(newRequest);
            state.pendingReceivedCount = state.pendingReceived.length;
            state.lastPendingReceivedFetched = null; // Invalidate cache
          }
        });

        // Show toast notification
        const displayName =
          payload.requester.firstName && payload.requester.lastName
            ? `${payload.requester.firstName} ${payload.requester.lastName}`
            : payload.requester.username;

        toast.info(`New connection request from ${displayName}`, {
          description: payload.message || 'Would like to connect with you',
          action: {
            label: 'View',
            onClick: () => {
              navigateToSafeAppPath('/dashboard/connections?tab=pending');
            },
          },
        });
      },

      _handleRequestAccepted: (payload: ConnectionAcceptedPayload) => {
        console.log('[ConnectionStore] WebSocket: Request accepted', payload);

        // Remove from pending sent
        set((state) => {
          state.pendingSent = state.pendingSent.filter(
            (r) => r.connectionId !== payload.connectionId
          );
          state.pendingSentCount = state.pendingSent.length;
          state.lastFetched = null; // Invalidate connections cache
          state.lastPendingSentFetched = null;
        });

        // Refresh connections list
        void get()
          .fetchConnections()
          .catch((error) => {
            logBackgroundConnectionError('refresh_connections_after_accept', error);
          });

        // Show toast
        const displayName =
          payload.acceptor.firstName && payload.acceptor.lastName
            ? `${payload.acceptor.firstName} ${payload.acceptor.lastName}`
            : payload.acceptor.username;

        toast.success(`${displayName} accepted your connection request!`);
      },

      _handleRequestDeclined: (payload: ConnectionDeclinedPayload) => {
        console.log('[ConnectionStore] WebSocket: Request declined', payload);

        // Remove from pending sent
        set((state) => {
          state.pendingSent = state.pendingSent.filter(
            (r) => r.connectionId !== payload.connectionId
          );
          state.pendingSentCount = state.pendingSent.length;
          state.lastPendingSentFetched = null;
        });
      },

      _handleRequestCancelled: (payload: ConnectionCancelledPayload) => {
        console.log('[ConnectionStore] WebSocket: Request cancelled', payload);

        // Remove from pending received (if the other party cancelled)
        set((state) => {
          state.pendingReceived = state.pendingReceived.filter(
            (r) => r.connectionId !== payload.connectionId
          );
          state.pendingReceivedCount = state.pendingReceived.length;
          state.lastPendingReceivedFetched = null;
        });
      },

      _handleConnectionRemoved: (payload: ConnectionRemovedPayload) => {
        console.log('[ConnectionStore] WebSocket: Connection removed', payload);

        // Remove from connections
        set((state) => {
          state.connections = state.connections.filter(
            (c) => c.connectionId !== payload.connectionId
          );
          state.connectionCount = state.connections.length;
          state.lastFetched = null;
        });
      },
    }))
  )
);

// ============================================================================
// Auth Store Integration - Reset on Logout
// ============================================================================

if (typeof window !== 'undefined') {
  void import('../auth/auth-store')
    .then(({ useAuthStore }) => {
      useAuthStore.subscribe(
        (state) => state.isAuthenticated,
        (isAuthenticated, previousIsAuthenticated) => {
          // If user logged out, reset connection state
          if (previousIsAuthenticated && !isAuthenticated) {
            console.log('[ConnectionStore] User logged out - resetting store');
            useConnectionStore.getState().reset();
          }
          // If user logged in, fetch connections
          else if (!previousIsAuthenticated && isAuthenticated) {
            console.log('[ConnectionStore] User logged in - fetching connections');
            // Small delay to allow auth to fully complete
            setTimeout(() => {
              void useConnectionStore
                .getState()
                .fetchConnections()
                .catch((error) => {
                  logBackgroundConnectionError('login_fetch_connections', error);
                });
              void useConnectionStore
                .getState()
                .fetchPendingReceived()
                .catch((error) => {
                  logBackgroundConnectionError('login_fetch_pending_received', error);
                });
            }, 500);
          }
        }
      );
    })
    .catch((error) => {
      logBackgroundConnectionError('auth_store_subscription', error);
    });
}

// ============================================================================
// WebSocket Listeners
// ============================================================================

let wsListenersRegistered = false;
let registeredConnectionSocketUnsubscribers: Array<() => void> = [];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object';

const isConnectionRequestReceivedPayload = (
  value: unknown
): value is ConnectionRequestReceivedPayload =>
  isRecord(value) &&
  typeof value.connectionId === 'string' &&
  isRecord(value.requester) &&
  typeof value.requestedAt === 'string' &&
  typeof value.expiresAt === 'string';

const isConnectionAcceptedPayload = (
  value: unknown
): value is ConnectionAcceptedPayload =>
  isRecord(value) &&
  typeof value.connectionId === 'string' &&
  isRecord(value.acceptor) &&
  typeof value.acceptedAt === 'string';

const isConnectionDeclinedPayload = (
  value: unknown
): value is ConnectionDeclinedPayload =>
  isRecord(value) &&
  typeof value.connectionId === 'string' &&
  typeof value.declinedAt === 'string';

const isConnectionCancelledPayload = (
  value: unknown
): value is ConnectionCancelledPayload =>
  isRecord(value) &&
  typeof value.connectionId === 'string';

const isConnectionRemovedPayload = (
  value: unknown
): value is ConnectionRemovedPayload =>
  isRecord(value) &&
  typeof value.connectionId === 'string' &&
  typeof value.removedBy === 'string';

export const setupConnectionWebSocketListeners = (): void => {
  if (wsListenersRegistered) {
    console.log('[ConnectionStore] WebSocket listeners already registered');
    return;
  }

  if (!socketClient.isConnected()) {
    console.log(
      '[ConnectionStore] Socket not connected, deferring WebSocket listener setup'
    );
    return;
  }

  console.log('[ConnectionStore] Setting up WebSocket listeners for connection events');

  // Register listener for request received
  socketClient.onCustomEvent(
    CONNECTION_EVENTS.REQUEST_RECEIVED,
    (...args: unknown[]) => {
      const payload = args[0];
      if (!isConnectionRequestReceivedPayload(payload)) {
        return;
      }

      console.log('[ConnectionWebSocket] Event received: REQUEST_RECEIVED', payload);
      useConnectionStore.getState()._handleRequestReceived(payload);
    }
  );

  // Register listener for request accepted
  socketClient.onCustomEvent(
    CONNECTION_EVENTS.REQUEST_ACCEPTED,
    (...args: unknown[]) => {
      const payload = args[0];
      if (!isConnectionAcceptedPayload(payload)) {
        return;
      }

      console.log('[ConnectionWebSocket] Event received: REQUEST_ACCEPTED', payload);
      useConnectionStore.getState()._handleRequestAccepted(payload);
    }
  );

  // Register listener for request declined
  socketClient.onCustomEvent(
    CONNECTION_EVENTS.REQUEST_DECLINED,
    (...args: unknown[]) => {
      const payload = args[0];
      if (!isConnectionDeclinedPayload(payload)) {
        return;
      }

      console.log('[ConnectionWebSocket] Event received: REQUEST_DECLINED', payload);
      useConnectionStore.getState()._handleRequestDeclined(payload);
    }
  );

  // Register listener for request cancelled
  socketClient.onCustomEvent(
    CONNECTION_EVENTS.REQUEST_CANCELLED,
    (...args: unknown[]) => {
      const payload = args[0];
      if (!isConnectionCancelledPayload(payload)) {
        return;
      }

      console.log('[ConnectionWebSocket] Event received: REQUEST_CANCELLED', payload);
      useConnectionStore.getState()._handleRequestCancelled(payload);
    }
  );

  // Register listener for connection removed
  socketClient.onCustomEvent(
    CONNECTION_EVENTS.CONNECTION_REMOVED,
    (...args: unknown[]) => {
      const payload = args[0];
      if (!isConnectionRemovedPayload(payload)) {
        return;
      }

      console.log('[ConnectionWebSocket] Event received: CONNECTION_REMOVED', payload);
      useConnectionStore.getState()._handleConnectionRemoved(payload);
    }
  );

  wsListenersRegistered = true;
  console.log('[ConnectionStore] WebSocket listeners registered successfully');
};

export const cleanupConnectionWebSocketListeners = (): void => {
  if (!wsListenersRegistered) {
    return;
  }

  console.log('[ConnectionStore] Cleaning up WebSocket listeners');

  socketClient.offCustomEvent(CONNECTION_EVENTS.REQUEST_RECEIVED);
  socketClient.offCustomEvent(CONNECTION_EVENTS.REQUEST_ACCEPTED);
  socketClient.offCustomEvent(CONNECTION_EVENTS.REQUEST_DECLINED);
  socketClient.offCustomEvent(CONNECTION_EVENTS.REQUEST_CANCELLED);
  socketClient.offCustomEvent(CONNECTION_EVENTS.CONNECTION_REMOVED);

  wsListenersRegistered = false;
};

const cleanupConnectionSocketConnectionListeners = (): void => {
  registeredConnectionSocketUnsubscribers.forEach((unsubscribe) =>
    unsubscribe()
  );
  registeredConnectionSocketUnsubscribers = [];
};

const handleConnectionSocketConnected = (): void => {
  console.log('[ConnectionStore] Socket connected, setting up WebSocket listeners');
  setupConnectionWebSocketListeners();
};

// Auto-setup WebSocket listeners when socket connects
if (typeof window !== 'undefined') {
  // Try to setup immediately if socket is already connected
  setTimeout(() => {
    if (socketClient.isConnected()) {
      setupConnectionWebSocketListeners();
    }
  }, 1000);

  // Also setup when socket connects later
  cleanupConnectionSocketConnectionListeners();
  registeredConnectionSocketUnsubscribers = [
    socketClient.on('connected', handleConnectionSocketConnected),
  ];
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cleanupConnectionSocketConnectionListeners();
    cleanupConnectionWebSocketListeners();
  });
}

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Get accepted connections list
 */
export const useConnections = () =>
  useConnectionStore((state) => state.connections);

/**
 * Get pending requests received
 */
export const usePendingReceived = () =>
  useConnectionStore((state) => state.pendingReceived);

/**
 * Get pending requests sent
 */
export const usePendingSent = () =>
  useConnectionStore((state) => state.pendingSent);

/**
 * Get user search results
 */
export const useSearchResults = () =>
  useConnectionStore((state) => state.searchResults);

/**
 * Get connection count
 */
export const useConnectionCount = () =>
  useConnectionStore((state) => state.connectionCount);

/**
 * Get pending received count (for badges/notifications)
 */
export const usePendingReceivedCount = () =>
  useConnectionStore((state) => state.pendingReceivedCount);

/**
 * Get pending sent count
 */
export const usePendingSentCount = () =>
  useConnectionStore((state) => state.pendingSentCount);

/**
 * Get blocked users list
 */
export const useBlockedUsers = () =>
  useConnectionStore((state) => state.blockedUsers);

/**
 * Get blocked users count
 */
export const useBlockedUsersCount = () =>
  useConnectionStore((state) => state.blockedUsersCount);

/**
 * Get loading state
 */
export const useConnectionLoading = () =>
  useConnectionStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      isSearching: state.isSearching,
    }))
  );

/**
 * Get error state
 */
export const useConnectionError = () =>
  useConnectionStore((state) => state.error);

/**
 * Get connection actions (for components that need multiple actions)
 */
export const useConnectionActions = () =>
  useConnectionStore(
    useShallow((state) => ({
      fetchConnections: state.fetchConnections,
      fetchPendingReceived: state.fetchPendingReceived,
      fetchPendingSent: state.fetchPendingSent,
      fetchBlockedUsers: state.fetchBlockedUsers,
      sendRequest: state.sendRequest,
      acceptRequest: state.acceptRequest,
      declineRequest: state.declineRequest,
      cancelRequest: state.cancelRequest,
      removeConnection: state.removeConnection,
      blockUser: state.blockUser,
      unblockUser: state.unblockUser,
      searchUsers: state.searchUsers,
      clearSearchResults: state.clearSearchResults,
      clearError: state.clearError,
    }))
  );
